import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Button,
    Box,
    VStack,
    Text,
    Flex,
    Input,
    Spinner,
    useToast,
    Divider,
    IconButton,
    HStack,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
} from "@chakra-ui/react";
import { useState, useContext, useEffect } from "react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { AddIcon, DeleteIcon, EditIcon, DownloadIcon } from "@chakra-ui/icons";
import moment from "moment";
import * as XLSX from "xlsx";

const CrmGlCodeDrawer = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { toastNotify, accountDetails } = useContext(AppContext);
    const isAdmin = accountDetails?.level === "admin";
    const toast = useToast();
    const [glCodes, setGlCodes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    
    // New state for GlCode fields
    const [description, setDescription] = useState("");
    const [glCode, setGlCode] = useState("");
    
    const [editingItem, setEditingItem] = useState<any>(null);
    const [editDescription, setEditDescription] = useState("");
    const [editGlCode, setEditGlCode] = useState("");

    const fetchGlCodes = async () => {
        try {
            setLoading(true);
            const response: any = await appRequest("crmGlCode", "getall", {});
            if (response?.status === "success") {
                setGlCodes(Array.isArray(response.data) ? response.data : []);
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to fetch GL Codes",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Fetch GL Codes error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchGlCodes();
        }
    }, [isOpen]);

    const handleAddGlCode = async () => {
        if (!description.trim() || !glCode.trim()) {
            toast({
                title: "Description and GL Code are required",
                status: "warning",
                duration: 2000,
            });
            return;
        }

        try {
            setAddLoading(true);
            const response: any = await appRequest("crmGlCode", "create", {
                processName: "crm",
                description: description.trim(),
                glCode: glCode.trim()
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "GL Code added successfully",
                    type: "success",
                });
                setDescription("");
                setGlCode("");
                fetchGlCodes();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to add GL Code",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Add GL Code error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleUpdateGlCode = async () => {
        if (!editDescription.trim() || !editGlCode.trim() || !editingItem?._id) return;

        try {
            setAddLoading(true);
            const response: any = await appRequest("crmGlCode", "update", {
                id: editingItem._id,
                description: editDescription.trim(),
                glCode: editGlCode.trim()
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "GL Code updated successfully",
                    type: "success",
                });
                setEditingItem(null);
                fetchGlCodes();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to update GL Code",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Update GL Code error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleDeleteGlCode = async (id: string) => {
        if (!id) return;
        try {
            const response: any = await appRequest("crmGlCode", "delete", { id });
            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "GL Code deleted successfully",
                    type: "success",
                });
                fetchGlCodes();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to delete GL Code",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Delete GL Code error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        }
    };

    const DEFAULT_GL_CODES = [
        { description: "MARKET RESEARCH AGENISE FEE", glCode: "450218" },
        { description: "CONFERENCES STALL EXPENSES", glCode: "450212" },
        { description: "PROMO EXPENSES OTC", glCode: "450515" },
        { description: "Brand Promotional Events", glCode: "450235" },
        { description: "MARKETING MEETING EXPENSES", glCode: "450230" },
        { description: "CONFERENCES SPONSERSHIP / PARTICIPATION", glCode: "450210" },
        { description: "PRODUCT ADVERTISEMENT", glCode: "450130" },
        { description: "PROF. FEES- Other Services", glCode: "440215" },
        { description: "SALES PROMOTION EXPENSES", glCode: "450200" },
        { description: "TRAVEL- RENT A CAB", glCode: "440054" },
        { description: "TRAVELLING EXPENSES", glCode: "440050" },
        { description: "CYCLE MEETING EXPENSES", glCode: "450220" },
        { description: "COMMISSION ON SALES", glCode: "450340" },
        { description: "TRAVEL- HOTEL STAY", glCode: "440052" },
        { description: "FIELD STAFF TRAINING EXPENSES", glCode: "450410" },
        { description: "OTHER ADVERTISEMENTS", glCode: "450120" },
        { description: "Consumer Offer", glCode: "440603" },
        { description: "CONFERENCES EVENT ARRANGMENT", glCode: "450211" },
        { description: "WAGES", glCode: "420120" },
        { description: "TESTING CHARGES MKT", glCode: "450206" },
        { description: "FREIGHT OUTWARD", glCode: "440160" },
        { description: "CLINICAL TRIALS", glCode: "450180" },
        { description: "MARKETING SUPPORT EXPENSES", glCode: "450252" },
        { description: "General Distribution Expenses", glCode: "440290" },
        { description: "SALES PROMOTION EXPENSES-FIELD", glCode: "450205" },
        { description: "PROFESSIONAL FEES", glCode: "440210" },
        { description: "PRODUCT DEVELOPMENT", glCode: "450150" },
        { description: "MEMBERSHIP & SUBSCRIPTIONS", glCode: "440230" },
        { description: "STAFF TRAINING EXPENSES", glCode: "420320" },
        { description: "COMPUTER EXPENSES", glCode: "440120" },
        { description: "MEDICAL CAMPS", glCode: "450216" },
        { description: "TRAVEL- FOOD", glCode: "440053" },
        { description: "CONFERENCES STAY HOTEL", glCode: "450213" },
        { description: "DISIGN & ARTWORK CHARGES", glCode: "450140" },
        { description: "CME - Others Workshops", glCode: "450240" },
        { description: "FOREIGN TRAVEL EXPENSES", glCode: "440070" },
        { description: "WATER CHARGES", glCode: "430250" },
    ];

    const handleSeedDefaults = async () => {
        try {
            setAddLoading(true);
            let count = 0;
            for (const item of DEFAULT_GL_CODES) {
                const exists = glCodes.find(g => g.glCode === item.glCode);
                if (exists) continue;

                await appRequest("crmGlCode", "create", {
                    processName: "crm",
                    description: item.description,
                    glCode: item.glCode
                });
                count++;
            }
            toastNotify({
                title: "Success",
                message: `${count} default GL codes added successfully`,
                type: "success",
            });
            fetchGlCodes();
        } catch (error: any) {
            console.error("Seed GL codes error:", error);
            toastNotify({
                title: "Error",
                message: "Failed to seed some GL codes",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleDownloadExcel = () => {
        if (!glCodes.length) return;
        
        const dataToExport = glCodes.map(item => ({
            "Description": item.description || "",
            "GL Code": item.glCode || ""
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);

        // Set column widths
        ws['!cols'] = [
            { wch: 40 }, // Description width
            { wch: 20 }  // GL Code width
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CRM GL Codes");
        XLSX.writeFile(wb, `crm_gl_codes_${moment().format("DD-MM-YYYY")}.xlsx`);
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
            <DrawerOverlay backdropFilter="blur(5px)" bg="rgba(0,0,0,0.1)" />
            <DrawerContent maxW="80vw" borderRadius="2xl" boxShadow="2xl">
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px" pb={2}>
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Text fontSize="xl" fontWeight="800" color="teal.500">
                                CRM GL Codes
                            </Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                                Manage GL codes and descriptions for the CRM process
                            </Text>
                        </Box>
                        <Button
                            display="none"
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            onClick={handleSeedDefaults}
                            isLoading={addLoading}
                            leftIcon={<AddIcon />}
                            borderRadius="lg"
                        >
                            Seed Defaults
                        </Button>
                    </Flex>
                </DrawerHeader>

                <DrawerBody>
                    <VStack spacing={6} align="stretch" py={4}>
                        {isAdmin && (
                            <>
                                <Box bg="teal.50" p={4} borderRadius="xl" border="1px solid" borderColor="teal.100">
                                    <Text fontSize="sm" fontWeight="700" mb={3} color="teal.700">
                                        {editingItem ? "Update GL Code" : "Add New GL Code"}
                                    </Text>
                                    <VStack spacing={3} align="stretch">
                                        <HStack>
                                            <Input
                                                placeholder="Description"
                                                value={editingItem ? editDescription : description}
                                                onChange={(e) => editingItem ? setEditDescription(e.target.value) : setDescription(e.target.value)}
                                                bg="white"
                                                borderRadius="lg"
                                                fontSize="sm"
                                                _focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px teal.400" }}
                                            />
                                            <Input
                                                placeholder="GL Code"
                                                value={editingItem ? editGlCode : glCode}
                                                onChange={(e) => editingItem ? setEditGlCode(e.target.value) : setGlCode(e.target.value)}
                                                bg="white"
                                                borderRadius="lg"
                                                fontSize="sm"
                                                _focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px teal.400" }}
                                            />
                                            <Button
                                                colorScheme="teal"
                                                onClick={editingItem ? handleUpdateGlCode : handleAddGlCode}
                                                isLoading={addLoading}
                                                leftIcon={editingItem ? <EditIcon /> : <AddIcon />}
                                                borderRadius="lg"
                                                px={6}
                                                fontSize="sm"
                                            >
                                                {editingItem ? "Update" : "Add"}
                                            </Button>
                                            {editingItem && (
                                                <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)}>
                                                    Cancel
                                                </Button>
                                            )}
                                        </HStack>
                                    </VStack>
                                </Box>

                                <Divider />
                            </>
                        )}

                        <Box>
                            <Flex justify="space-between" align="center" mb={4}>
                                <Text fontSize="sm" fontWeight="700" color="gray.700">
                                    Existing GL Codes
                                </Text>
                                <Button
                                    size="sm"
                                    colorScheme="teal"
                                    variant="outline"
                                    onClick={handleDownloadExcel}
                                    isDisabled={glCodes.length === 0}
                                    leftIcon={<DownloadIcon />}
                                    borderRadius="lg"
                                >
                                    Download Excel
                                </Button>
                            </Flex>
                            {loading ? (
                                <Flex justify="center" align="center" py={10}>
                                    <Spinner color="teal.500" />
                                </Flex>
                            ) : glCodes.length > 0 ? (
                                <TableContainer border="1px solid" borderColor="gray.100" borderRadius="xl" bg="gray.50">
                                    <Table variant="simple" size="sm">
                                        <Thead bg="gray.100">
                                            <Tr>
                                                <Th py={4}>Description</Th>
                                                <Th py={4}>GL Code</Th>
                                                <Th py={4}>Created By</Th>
                                                <Th py={4}>Created At</Th>
                                                {isAdmin && <Th py={4} textAlign="right">Actions</Th>}
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {glCodes.map((item) => (
                                                <Tr
                                                    key={item._id}
                                                    _hover={{ bg: "white" }}
                                                    transition="all 0.2s"
                                                >
                                                    <Td>
                                                        <Text fontWeight="600">{item.description}</Text>
                                                    </Td>
                                                    <Td>
                                                        <Badge variant="subtle" colorScheme="purple" borderRadius="md">
                                                            {item.glCode}
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.600">
                                                            {item.createdBy?.name || item.createdBy?.username || "System"}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.600">
                                                            {item.created_At ? moment(item.created_At).format("DD-MM-YYYY HH:mm") : "N/A"}
                                                        </Text>
                                                    </Td>
                                                    {isAdmin && (
                                                        <Td textAlign="right">
                                                            <HStack spacing={1} justify="flex-end">
                                                                <IconButton
                                                                    aria-label="Edit item"
                                                                    icon={<EditIcon />}
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    colorScheme="blue"
                                                                    onClick={() => {
                                                                        setEditingItem(item);
                                                                        setEditDescription(item.description);
                                                                        setEditGlCode(item.glCode);
                                                                    }}
                                                                />
                                                                <IconButton
                                                                    aria-label="Delete item"
                                                                    icon={<DeleteIcon />}
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    colorScheme="red"
                                                                    onClick={() => handleDeleteGlCode(item._id)}
                                                                />
                                                            </HStack>
                                                        </Td>
                                                    )}
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Flex direction="column" align="center" justify="center" py={10} color="gray.400">
                                    <Text fontSize="sm">No GL Codes found</Text>
                                </Flex>
                            )}
                        </Box>
                    </VStack>
                </DrawerBody>

                <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={onClose} borderRadius="lg">
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export default CrmGlCodeDrawer;
